/**
 * Блок вопросов клиентов или причин отказа (#843, #844) — та же карточка и
 * раскрытие до разговоров, что у остальных вкладок «Голоса клиента».
 */
import { useTranslation } from 'react-i18next';
import type { VoiceCustomerQuestions } from '@aica/api-types/schemas';
import { VoiceSectionPanel } from './VoiceSectionPanel';
import type { VoiceSection } from './voice-view-model';
import {
  QUESTION_WORDING_PREVIEW,
  firstMinuteTextKeys,
} from './voice-questions-view-model';
import type { VoiceSearch } from './voice-search';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** Заголовочное N блока — своими словами: разговоры, звонки, отказы. */
function questionWindowLabel(
  section: VoiceSection,
  cq: VoiceCustomerQuestions,
  t: Translate
): string {
  switch (section.id) {
    case 'firstMinute':
      return t(firstMinuteTextKeys(cq).window, {
        count: cq.firstMinute.callsInWindow,
      });
    case 'unanswered':
      return t('voicePage.questions.windowUnanswered');
    case 'declines':
      return t('voicePage.declines.windowCalls', { count: cq.declines.callsInWindow });
    default:
      return t('voicePage.questions.windowCalls', { count: cq.questions.callsInWindow });
  }
}

interface QuestionPanelProps {
  section: VoiceSection;
  cq: VoiceCustomerQuestions;
  search: VoiceSearch;
  onSearchChange: (patch: Partial<VoiceSearch>) => void;
  onOpenCall: (recordId: string) => void;
  hint?: string;
  /** The first rows only, then «Show all N» (`VoiceSectionPanel`). */
  limit?: number;
}

/** Блок вопросов или причин — та же карточка и раскрытие, что у вкладок раздела. */
export function VoiceQuestionPanel({
  section,
  cq,
  search,
  onSearchChange,
  onOpenCall,
  hint,
  limit,
}: QuestionPanelProps) {
  const { t } = useTranslation('dashboard');
  return (
    <VoiceSectionPanel
      section={section}
      callsInWindow={section.callsInWindow ?? 0}
      windowLabel={questionWindowLabel(section, cq, t)}
      expandedRow={search.row}
      onToggleRow={(rowId) =>
        onSearchChange({ row: search.row === rowId ? undefined : rowId })
      }
      onOpenCall={onOpenCall}
      preview={section.id === 'questions' ? QUESTION_WORDING_PREVIEW : 0}
      hint={hint}
      limit={limit}
      lowerBound={cq.truncated}
    />
  );
}
